import React from "react";
import { Container, Header, Content, Placeholder, Row, Col, Heading, HeadingGroup, Panel, Text, IconButton, SelectPicker, Loader, Drawer, CheckboxGroup, Checkbox } from 'rsuite';
import { Table, Column, HeaderCell, Cell, } from 'rsuite-table';
import NavBarComponent from "../components/NavBar";
import { useParams } from "react-router-dom";
import Chart from 'chart.js/auto';
import { BarWithErrorBarsChart } from 'chartjs-chart-error-bars';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl';
import './css/businesscockpit.css'
import 'mapbox-gl/dist/mapbox-gl.css';
import ArrowLeftLineIcon from '@rsuite/icons/legacy/ArrowLeftLine';
import InfoOutlineIcon from '@rsuite/icons/InfoOutline';
import ExportExcel from "../utils/ExcelBtn";

import { BsArrowDownRightSquareFill } from "react-icons/bs";
import { BsArrowUpRightSquareFill } from "react-icons/bs";
import { BsArrowRightSquareFill } from "react-icons/bs";


const URL_FRONT = process.env.REACT_APP_URL_LOCAL;
const URL_BACK = process.env.REACT_APP_URL_SERVER_BACK;
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const BASE_YEAR_OPTIONS = [
  { label: 'Average', value: 'average' },
  { label: 2019, value: '2019' },
  { label: 2020, value: '2020' },
  { label: 2021, value: '2021' },
  { label: 2022, value: '2022' },
  { label: 2023, value: '2023' }
];

const EXPORT_AGGREGATIONS = [
  { key: 'modal_split_trips', scale: 100 },
  { key: 'modal_split_pkm', scale: 100 },
  { key: 'trips', scale: 1 },
  { key: 'pkm', scale: 1 },
  { key: 'onsite_trips', scale: 1 },
  { key: 'onsite_pkm', scale: 1 }
];

const EXPORT_AGGREGATION_LABELS = {
  modal_split_trips: 'Modal Split Trips',
  modal_split_pkm: 'Modal Split PKm',
  trips: 'Total Trips',
  pkm: 'Total PKm',
  onsite_trips: 'Onsite Trips',
  onsite_pkm: 'Onsite PKm'
};

function numberWithCommas(x) {
  if (typeof x !== "undefined") {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");

  }
  else {
    return 0
  }
}

function stripTrailingYear(slug) {
  return slug?.replace(/_(\d{4})$/, '') || '';
}

function roundNumber(value, digits = 4) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Number(value.toFixed(digits))
    : null;
}

function scaleNumber(value, scale = 1, digits = 4) {
  return typeof value === 'number' && Number.isFinite(value)
    ? roundNumber(value * scale, digits)
    : null;
}

function sanitizeFileName(value) {
  return (value || 'company-analysis')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}



//Business Cockpit - Details
class BusinessCockpitDetails extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      details: {},
      top_ods: [],
      options: ['color'],
      years: BASE_YEAR_OPTIONS,
      map_types: [
        { label: 'Location', value: 'polygon' },
        { label: 'Employees', value: 'employees' },
      ],
      aggregation_types: [
        { label: 'Modal Split (PKm) - Bar', value: 'modal_split_pkm' },
        { label: 'Modal Split (Trips) - Bar', value: 'modal_split_trips' },
        { label: 'Modal Split (PKm) - Pie', value: 'modal_split_pkm_pie' },
        { label: 'Modal Split (Trips) - Pie', value: 'modal_split_trips_pie' },
        { label: 'Total Trips', value: 'trips' },
        { label: 'Total PKm', value: 'pkm' },
        { label: 'Trips based on Commuter Factor', value: 'onsite_trips' },
        { label: 'PKm based on Commuter Factor', value: 'onsite_pkm' },
        { label: 'Dwell Times', value: 'dwell_times' }

      ],
      current_year: this.getCurrentYear(),
      current_maps: 'polygon',
      current_radio: 'trips',
      current_aggregation: 'modal_split_trips',
      loading: true,
      zoom1: 13,
      zoom2: 10,
      mapita1: 10.5,
      mapita2: 10.5,
      loading_map: true,
      tyle: 'mapbox://styles/mapbox/streets-v11',
      dwell_times: '',
      documentation: false,
      donutpieinfo: '',
      downloadError: ''
    }
    this.chartRef = React.createRef();
    this.chartInstance = null;
    this.chartRefDonut = React.createRef();
    this.chartInstanceDonut = null;
    this.mapContainer = React.createRef(); // Use a ref for the map container
    this.mapContainerOVQuality = React.createRef(); // Use a ref for the map container

  }

  componentDidMount() {
    this.initializeMap();
    this.getDetailsData();
    this.checkYearAvailability('2024');
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.options !== prevState.options) {
      this.initializeMapOvQuality();
      this.handleDonutOVQuality();
    }
  }

  handleOVOptions(event) {
    this.setState({ options: event })
  }

  handleDocumentation() {
    this.setState({ documentation: !this.state.documentation })
  }

  getRequestHeaders(includeContentType = false) {
    const headers = {
      Accept: 'application/json'
    };

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.props.idToken) {
      headers.Authorization = `Bearer ${this.props.idToken}`;
    }

    return headers;
  }

  getBaseSlug() {
    return stripTrailingYear(this.props.params['*']);
  }

  buildSlugForYear(year) {
    const baseSlug = this.getBaseSlug();
    return year === 'average' ? baseSlug : `${baseSlug}_${year}`;
  }

  getOvQualityRequestBody() {
    return {
      threshold_duration_percentage_green: [
        0,
        130
      ],
      threshold_duration_percentage_yellow: [
        130,
        200
      ],
      threshold_duration_percentage_red: [
        200,
        1000
      ],
      max_difference_green: 10,
      max_changes_green: 1,
      max_changes_yellow: 2,
      min_changes_red: 2,
      short_bike_radius: 5000,
      medium_bike_radius: 10000,
      minutes_in_train: 1200,
      traffic_mode: "best_guess",
      options: this.state.options
    };
  }

  getCurrentYear() {
    let check_year = this.props.params["*"].split('_');
    let return_value = 'average';

    if ((check_year[check_year.length - 1]).length === 4) {
      return_value = check_year[check_year.length - 1]
      this.setState({ current_year: return_value })
    } else {
      this.setState({ current_year: 'average' })
    }

    return return_value;
  }

  async getDetailsData() {
    try {
      const response = await fetch(URL_BACK + 'sbb_cockpit/record/' + this.props.params['*'], {
        method: 'GET',
        headers: this.getRequestHeaders(),
      });
      const data = await response.json();
      this.setState(
        { details: data[0], top_ods: data[0]?.top_ods, loading: false },
        () => {
          this.initializeChart(); // Callback to initialize chart after state is updated
          this.initializeMap();   // Callback to initialize map after state is updated
          this.initializeMapOvQuality();
          this.handleDonutOVQuality();
        }
      );
    } catch (error) {
      console.error('Error fetching details:', error);
      this.setState({ loading: false });
    }
  }

  handleSelectMap(event) {

    if (event === 'polygon') {
      this.setState({ current_maps: event });
      this.state.mapita1.setLayoutProperty('data-map-1-fill', 'visibility', 'visible');
      this.state.mapita1.setLayoutProperty('data-map-1-line', 'visibility', 'visible');
      this.state.mapita1.setLayoutProperty('data-map-2-fill', 'visibility', 'none');
    } else {
      this.state.mapita1.setLayoutProperty('data-map-1-fill', 'visibility', 'none');
      this.state.mapita1.setLayoutProperty('data-map-1-line', 'visibility', 'none');
      this.state.mapita1.setLayoutProperty('data-map-2-fill', 'visibility', 'visible');
    }
  }

  handleSelectPickerYear(event) {
  // 1️⃣ Work out whether the current page is private or public
  //    (feel free to tighten the test if you have more precise routing info)
  const isPrivate = window.location.pathname.includes('/b2b/');
  const BASE_PATH = isPrivate ? 'b2b/detail/' : 'modal_split/';

  // 2️⃣ Extract the catch-all param once
  const slug = this.props.params['*'];          // e.g. "foo_bar_2023"
  const parts = slug.split('_');
  const lastPart = parts[parts.length - 1];
  const hasYear = /^\d{4}$/.test(lastPart);     // true if last segment is YYYY

  let targetSlug;

  if (event === 'average') {
    // strip “_YYYY” when present
    targetSlug = hasYear ? slug.slice(0, -5) : slug;
  } else {
    if (hasYear) {
      // replace the year with the picker value
      targetSlug = slug.slice(0, -4) + event;
    } else {
      // just tack the value on
      targetSlug = `${slug}_${event}`;
    }
  }

  // 3️⃣ Single redirect line
  window.location.href = `${URL_FRONT}${BASE_PATH}${targetSlug}`;
}


  handleDonutOVQuality() {
    const fixed_avg = this.getBaseSlug();
    fetch(URL_BACK + 'sbb_cockpit/ov_quality_stack/' + fixed_avg, {
      method: 'POST',
      headers: this.getRequestHeaders(true),
      body: JSON.stringify(this.getOvQualityRequestBody())
    }).then((res) => res.json())
      .then((json) => {
        this.setState({
          donutpieinfo: json
        })

        // if (this.chartInstanceDonut) {
        //   this.chartInstanceDonut.destroy();
        // }
        // const ctx = this.chartRefDonut.current.getContext('2d');
        // this.chartInstanceDonut = new Chart(ctx, {
        //   type: 'doughnut',
        //   data: {
        //     labels: ['Green ÖV', 'Yellow ÖV', 'Red ÖV', 'Undefined ÖV'],
        //     datasets: [
        //       {
        //         label: 'ÖV Quality',
        //         data: [json.green, json.yellow, json.red, json.gray],
        //         backgroundColor: ['rgba(58, 148, 16,0.6)', 'rgba(240, 191, 13,0.6)', 'rgba(232, 10, 10, 0.6)', 'rgba(90, 90, 90,0.6)'],
        //         borderColor: ['rgb(255, 255, 255)']
        //       }
        //     ]
        //   },
        //   options: {
        //     maintainAspectRatio: false,
        //   }
        // });

      })


  }

  handleSelectPicker(event) {
    if (event === 'dwell_times') {

      fetch(URL_BACK + 'sbb_cockpit/dwell_times/' + this.props.params['*'], {
        method: 'GET',
        headers: this.getRequestHeaders(),
      }).then((res) => res.json())
        .then((json) => {
          this.setState({
            dwell_times: json
          })

          if (this.chartInstance) {
            this.chartInstance.destroy();
          }

          const ctx = this.chartRef.current.getContext('2d');

          let result = {
            counts: json.map(item => item.counts),
            dwell_density: json.map(item => item.dwell_density),
            hours: json.map(item => item.hour)
          };


          this.chartInstance = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: result.hours,
              datasets: [{
                label: 'Counts',
                data: result.counts,
                borderWidth: 1
              },
              {
                label: 'Dwell density',
                data: result.dwell_density,
                borderWidth: 1
              }]
            },
            options: {
              scales: {
                y: {
                  beginAtZero: true
                }
              }
            }
          });

        })


    } else if(event.includes('pie')){
      let valueToEvaluate = {};

      if (event === 'modal_split_pkm_pie') {
        valueToEvaluate = this.state.details?.modal_split_pkm || {};
      } else if (event === 'modal_split_trips_pie') {
        valueToEvaluate = this.state.details?.modal_split_trips || {};
      }

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }
      const ctx = this.chartRef.current.getContext('2d');

      this.chartInstance = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: ['ÖV', 'MIV', 'LV'],
          datasets: [{
            label: 'Modal Split Overview',
            data: [valueToEvaluate.OV.mean * 100, valueToEvaluate.MIV.mean * 100, valueToEvaluate.LV.mean * 100],
            backgroundColor: ['rgba(247, 37, 133, 0.6)', 'rgba(75, 192, 192, 0.6)', 'rgba(114, 9, 183, 0.6)'],
            borderColor: ['rgba(247, 37, 133)', 'rgba(75, 192, 192)', 'rgba(114, 9, 183)'],
            hoverOffset: 4,
            borderWidth: 1
          }]
        },
        options: {
          scales: {
            y: {
              beginAtZero: true
            }
          }
        }
      });

    }
    else {
      let valueToEvaluate = {};

      // Safeguard against invalid event values
      if (event === 'modal_split_pkm') {
        valueToEvaluate = this.state.details?.modal_split_pkm || {};
      } else if (event === 'modal_split_trips') {
        valueToEvaluate = this.state.details?.modal_split_trips || {};
      } else if (event === 'trips') {
        valueToEvaluate = this.state.details?.trips || {};
      } else if (event === 'pkm') {
        valueToEvaluate = this.state.details?.pkm || {};
      } else if (event === 'onsite_trips') {
        valueToEvaluate = this.state.details?.onsite_trips || {};
      } else if (event === 'onsite_pkm') {
        valueToEvaluate = this.state.details?.onsite_pkm || {};
      }

      // Set current aggregation state
      this.setState({ current_aggregation: event });

      // Check if the necessary data exists before accessing properties
      if (
        valueToEvaluate.MIV && valueToEvaluate.LV && valueToEvaluate.OV &&
        valueToEvaluate.MIV.mean && valueToEvaluate.LV.mean && valueToEvaluate.OV.mean &&
        valueToEvaluate.MIV.std && valueToEvaluate.LV.std && valueToEvaluate.OV.std
      ) {
        // Destroy the existing chart instance if it exists
        if (this.chartInstance) {
          this.chartInstance.destroy();
        }

        const ctx = this.chartRef.current.getContext('2d');

        this.chartInstance = new BarWithErrorBarsChart(ctx, {
          data: {
            labels: ['ÖV', 'MIV', 'LV'],
            datasets: [
              {
                label: 'Values with Error Bars',
                data: [
                  {
                    y: (event.includes('modal_split') ? valueToEvaluate.OV.mean * 100 : valueToEvaluate.OV.mean),
                    yMin: (event.includes('modal_split') ? (valueToEvaluate.OV.mean - valueToEvaluate.OV.std) * 100 : (valueToEvaluate.OV.mean - valueToEvaluate.OV.std)),
                    yMax: (event.includes('modal_split') ? (valueToEvaluate.OV.mean + valueToEvaluate.OV.std) * 100 : (valueToEvaluate.OV.mean + valueToEvaluate.OV.std)),
                  },
                  {
                    y: (event.includes('modal_split') ? valueToEvaluate.MIV.mean * 100 : valueToEvaluate.MIV.mean),
                    yMin: (event.includes('modal_split') ? (valueToEvaluate.MIV.mean - valueToEvaluate.MIV.std) * 100 : (valueToEvaluate.MIV.mean - valueToEvaluate.MIV.std)),
                    yMax: (event.includes('modal_split') ? (valueToEvaluate.MIV.mean + valueToEvaluate.MIV.std) * 100 : (valueToEvaluate.MIV.mean + valueToEvaluate.MIV.std)),
                  },
                  {
                    y: (event.includes('modal_split') ? valueToEvaluate.LV.mean * 100 : valueToEvaluate.LV.mean),
                    yMin: (event.includes('modal_split') ? (valueToEvaluate.LV.mean - valueToEvaluate.LV.std) * 100 : (valueToEvaluate.LV.mean - valueToEvaluate.LV.std)),
                    yMax: (event.includes('modal_split') ? (valueToEvaluate.LV.mean + valueToEvaluate.LV.std) * 100 : (valueToEvaluate.LV.mean + valueToEvaluate.LV.std)),
                  },
                ],
                backgroundColor: ['rgba(247, 37, 133, 0.6)', 'rgba(75, 192, 192, 0.6)', 'rgba(114, 9, 183, 0.6)'],
                borderColor: ['rgba(247, 37, 133)', 'rgba(75, 192, 192)', 'rgba(114, 9, 183)'],
                borderWidth: 1,
              },
            ],
          },
          options: {
            responsive: true,
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  stepSize: 10,
                },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: function (tooltipItem) {
                    const errorBar = (
                      tooltipItem.dataset.data[tooltipItem.dataIndex].y -
                      tooltipItem.dataset.data[tooltipItem.dataIndex].yMin
                    ).toFixed(2);
                    if (event === 'modal_split') {
                      return `Value: ${tooltipItem.dataset.data[tooltipItem.dataIndex].y.toFixed(0)}%, Std. Deviation: (±${errorBar}%)`;
                    } else {
                      return `Value: ${numberWithCommas(tooltipItem.dataset.data[tooltipItem.dataIndex].y.toFixed(0))}, Std. Deviation: (±${errorBar})`;

                    }
                  },
                },
              },
            },
          },
        });
      } else {
        console.error('Missing or invalid data structure in valueToEvaluate:', valueToEvaluate);
      }
    }
  }



  initializeMap() {
    if (this.state.details && this.mapContainer.current) {
      const map = new mapboxgl.Map({
        container: this.mapContainer.current,
        style: this.state.tyle,
        center: this.state.details.centroid,
        zoom: this.state.zoom1,
      });

      map.on('load', () => {
        // Add zoom and rotation controls to the map.
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false, showZoom: false }), 'top-left');

        //Add shapes to the map
        map.addSource('data-map-1', {
          'type': 'geojson',
          'data': URL_BACK + 'sbb_cockpit/analyzed_polygon/' + this.props.params['*'],
          'generateId': true
        });

        map.addLayer({
          'id': 'data-map-1-fill',
          'type': 'fill',
          'source': 'data-map-1',
          'layout': {
            'visibility': "visible"
          },
          'paint': {
            "fill-color": '#E32D8C',
            'fill-opacity': 0.3
          }
        });

        map.addLayer({
          'id': 'data-map-1-line',
          'type': 'line',
          'source': 'data-map-1',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#E32D8C',
            'line-width': 2
          }
        });

        //Add shapes to the map
        map.addSource('data-map-2', {
          'type': 'geojson',
          'data': URL_BACK + 'sbb_cockpit/employees/' + this.props.params['*'],
          'generateId': true
        });

        map.addLayer({
          'id': 'data-map-2-fill',
          'type': 'circle',
          'source': 'data-map-2',
          'paint': {
            'circle-radius': 5,
            'circle-color': '#e32a8d' // blue color
          },
          'layout': {
            'visibility': "none"
          },
        });

      });

      this.setState({
        mapita1: map,
        zoom: map.getZoom(),
      });

      map.on('click', 'data-map-2-fill', (e) => {

        let html = '<p style="margin-block-end: 0em;"><span style="font-weight: bold;">Number of employees: </span>' + e.features[0].properties.B08EMPT + '</p>'

        new mapboxgl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(html).addTo(map);
      })

    }
  }

  initializeMapOvQuality() {
    this.setState({ loading_map: true })
    if (this.state.details && this.mapContainerOVQuality.current) {
      const fixed_avg = this.getBaseSlug();
      fetch(URL_BACK + 'sbb_cockpit/ov_quality_map/' + fixed_avg, {
        method: 'POST',
        headers: this.getRequestHeaders(true),
        body: JSON.stringify(this.getOvQualityRequestBody())
      }).then((res) => res.json())
        .then((json) => {
          this.setState({ loading_map: false })

          const map = new mapboxgl.Map({
            container: this.mapContainerOVQuality.current,
            style: "mapbox://styles/mapbox/light-v10",
            center: this.state.details.centroid,
            zoom: this.state.zoom2,
          });

          map.on('load', () => {
            // Add zoom and rotation controls to the map.
            map.addControl(new mapboxgl.NavigationControl({ showCompass: false, showZoom: false }), 'top-left');

            //Add shapes to the map
            map.addSource('data-map-1', {
              'type': 'geojson',
              'data': json,
              'generateId': true
            });

            map.addLayer({
              'id': 'data-map-1-fill',
              'type': 'fill',
              'source': 'data-map-1',
              'layout': {
                'visibility': "visible"
              },
              'paint': {
                "fill-color": ['get', 'color_combined'],
                'fill-opacity': ['get', 'opacity']
              }
            });

          });


          this.setState({
            mapita2: map,
            zoom: map.getZoom(),
          });

          map.on('click', 'data-map-1-fill', (e) => {

            let html_button = '<a href="' + e.features[0].properties.google_link + '" target="_blank"><button style="background-color: #0c8a8a;color: white;border: 1px solid #e4e4e4;padding: 8px;border-radius: 6px;cursor: pointer;">Google Maps Route</button></a>';
            let html = '<p style="margin-block-end: 0em;"><span style="font-weight: bold;">People: </span>' + (e.features[0].properties.people).toFixed(0) + '</p><p style="margin-block-end: 0em;"><span style="font-weight: bold;">Duration Train: </span>' + e.features[0].properties.duration_train_text + '</p><p style="margin-block-end: 0em;"><span style="font-weight: bold;">Duration Car: </span>' + e.features[0].properties.duration_car_text + '</p>'

            new mapboxgl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(html + html_button).addTo(map);
          })
        })

    }
  }

  initializeChart() {
    if (this.state.details && this.chartRef.current) {
      const ctx = this.chartRef.current.getContext('2d');
      const { modal_split_trips } = this.state.details;

      this.chartInstance = new BarWithErrorBarsChart(ctx, {
        data: {
          labels: ['ÖV', 'MIV', 'LV'],
          datasets: [
            {
              label: 'Values with Error Bars',
              data: [
                {
                  y: modal_split_trips?.OV.mean * 100,
                  yMin: (modal_split_trips?.OV.mean - modal_split_trips?.OV.std) * 100,
                  yMax: (modal_split_trips?.OV.mean + modal_split_trips?.OV.std) * 100,
                },
                {
                  y: modal_split_trips?.MIV.mean * 100,
                  yMin: (modal_split_trips?.MIV.mean - modal_split_trips?.MIV.std) * 100,
                  yMax: (modal_split_trips?.MIV.mean + modal_split_trips?.MIV.std) * 100,
                },
                {
                  y: modal_split_trips?.LV.mean * 100,
                  yMin: (modal_split_trips?.LV.mean - modal_split_trips?.LV.std) * 100,
                  yMax: (modal_split_trips?.LV.mean + modal_split_trips?.LV.std) * 100,
                },

              ],
              backgroundColor: ['rgba(247, 37, 133, 0.6)', 'rgba(75, 192, 192, 0.6)', 'rgba(114, 9, 183, 0.6)'],
              borderColor: ['rgba(247, 37, 133)', 'rgba(75, 192, 192)', 'rgba(114, 9, 183)'],
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 10,
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function (tooltipItem) {
                  const errorBar = (
                    tooltipItem.dataset.data[tooltipItem.dataIndex].y -
                    tooltipItem.dataset.data[tooltipItem.dataIndex].yMin
                  ).toFixed(2);
                  return `Value: ${tooltipItem.dataset.data[tooltipItem.dataIndex].y.toFixed(0)}%, Std. Deviation: (±${errorBar}%)`;
                },
              },
            },
          },
        },
      });
    }
  }

  async fetchDetailRecord(slug) {
    const response = await fetch(URL_BACK + 'sbb_cockpit/record/' + slug, {
      method: 'GET',
      headers: this.getRequestHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch detail record for ${slug} (${response.status})`);
    }

    const data = await response.json();
    return data?.[0] || {};
  }

  async checkYearAvailability(year) {
    if (this.state.years.some((option) => option.value === year)) {
      return;
    }

    try {
      const detail = await this.fetchDetailRecord(this.buildSlugForYear(year));

      if (detail && Object.keys(detail).length > 0) {
        this.setState((prevState) => ({
          years: [
            ...prevState.years,
            { label: Number(year), value: year }
          ].sort((left, right) => {
            if (left.value === 'average') {
              return -1;
            }

            if (right.value === 'average') {
              return 1;
            }

            return Number(left.value) - Number(right.value);
          })
        }));
      }
    } catch (error) {
      console.warn(`Year ${year} is not available for ${this.getBaseSlug()}:`, error);
    }
  }

  async fetchOvQualitySummary() {
    const response = await fetch(URL_BACK + 'sbb_cockpit/ov_quality_stack/' + this.getBaseSlug(), {
      method: 'POST',
      headers: this.getRequestHeaders(true),
      body: JSON.stringify(this.getOvQualityRequestBody())
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch OV quality summary (${response.status})`);
    }

    return response.json();
  }

  flattenAggregationMetric(metricKey, metricValue, scale = 1) {
    const metricLabel = EXPORT_AGGREGATION_LABELS[metricKey] || metricKey;
    const valueSuffix = scale === 100 ? ' (%)' : '';

    return ['OV', 'MIV', 'LV'].reduce((row, mode) => {
      row[`${metricLabel} - ${mode} Mean${valueSuffix}`] = scaleNumber(metricValue?.[mode]?.mean, scale);
      row[`${metricLabel} - ${mode} Std${valueSuffix}`] = scaleNumber(metricValue?.[mode]?.std, scale);
      return row;
    }, {});
  }

  buildExportRow(year, detail, ovQuality) {
    return {
      Year: year === 'average' ? 'Average' : year,
      'Detail Slug': this.buildSlugForYear(year),
      'Company Name': detail?.company_name ?? this.state.details?.company_name ?? '',
      'Site Name': detail?.site_name ?? this.state.details?.site_name ?? '',
      Municipality: detail?.municipality ?? this.state.details?.municipality ?? '',
      PLZ: detail?.plz ?? this.state.details?.plz ?? '',
      Canton: detail?.kanton ?? this.state.details?.kanton ?? '',
      'Location Type': detail?.type ?? this.state.details?.type ?? '',
      Employees: roundNumber(detail?.employees, 0),
      'Daily Commuters': roundNumber(detail?.total_onsite_trips, 0),
      'PKm per Day': roundNumber(detail?.total_pkm, 0),
      'Potential New Revenue (CHF)': roundNumber(detail?.potential?.new_revenue, 0),
      'Modal Split Trend Direction': detail?.modal_split_trend?.trend ?? '',
      'Modal Split Trend (%)': roundNumber(detail?.modal_split_trend?.value, 2),
      'OV Quality Scenario': this.state.options.join(', '),
      'OV Quality Great (%)': scaleNumber(ovQuality?.green_percentage, 100, 2),
      'OV Quality Decent (%)': scaleNumber(ovQuality?.yellow_percentage, 100, 2),
      'OV Quality Bad (%)': scaleNumber(ovQuality?.red_percentage, 100, 2),
      'OV Quality Undefined (%)': scaleNumber(ovQuality?.gray_percentage, 100, 2)
    };
  }

  async getDetailExportData() {
    this.setState({ downloadError: '' });

    let ovQuality = null;
    const failedYears = [];

    try {
      ovQuality = await this.fetchOvQualitySummary();
    } catch (error) {
      console.error('Error fetching OV quality summary for export:', error);
      this.setState({ downloadError: 'OV quality could not be loaded. The Excel will contain the remaining yearly metrics.' });
    }

    const yearValues = this.state.years.map((year) => year.value);
    const rows = await Promise.all(yearValues.map(async (year) => {
      try {
        const detail = await this.fetchDetailRecord(this.buildSlugForYear(year));
        return EXPORT_AGGREGATIONS.reduce((currentRow, aggregation) => ({
          ...currentRow,
          ...this.flattenAggregationMetric(aggregation.key, detail?.[aggregation.key], aggregation.scale)
        }), this.buildExportRow(year, detail, ovQuality));
      } catch (error) {
        console.error(`Error fetching export data for year ${year}:`, error);
        failedYears.push(year);
        return EXPORT_AGGREGATIONS.reduce((currentRow, aggregation) => ({
          ...currentRow,
          ...this.flattenAggregationMetric(aggregation.key, null, aggregation.scale)
        }), this.buildExportRow(year, {}, ovQuality));
      }
    }));

    if (failedYears.length > 0) {
      this.setState({
        downloadError: `Some years could not be loaded for export: ${failedYears.join(', ')}. Their rows were exported with blank values.`
      });
    }

    return rows;
  }

  render() {
    const exportFileName = sanitizeFileName(
      `SBB Business Cockpit - ${this.state.details?.company_name || 'Company'} - ${this.state.details?.site_name || 'Analysis'}`
    );

    return (
      <>
        {
          this.state.loading ?
            <Loader backdrop content="loading..." vertical />
            :
            <>
              <Drawer placement={'right'} open={this.state.documentation} size={'md'} onClose={() => this.handleDocumentation()}>
                <Drawer.Header>
                  <Drawer.Title>Details - SBB Cockpit</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body>
                  <Heading level={4} style={{ paddingTop: '10px' }}>Key Data</Heading>
                  <Text>The detail view shows key data for the selected company site, including </Text>
                  <Text>- <span style={{ fontWeight: 'bold' }}>Employees</span> The number of employees in that site (drawn from the Swiss Federal Statistical Office’s <a href="https://www.bfs.admin.ch/bfs/en/home/services/geostat/swiss-federal-statistics-geodata/business-employment/structural-business-statistics-statent-from-2011-onwards.html">Structural business statistics</a>). </Text>
                  <Text>- <span style={{ fontWeight: 'bold' }}>Daily Commuters </span> shows the number of people who commute to that company site on an average working day. This number is calculated using the commuter factor, which indicates how many percent of the workforce come to work on any given day.</Text>
                  <Text>- <span style={{ fontWeight: 'bold' }}>PKm per day</span> indicates the total number of person kilometers commuters travel to the company site on an average work day.</Text>
                  <Text>- <span style={{ fontWeight: 'bold' }}>Commuter Factor</span> indicates how many percent of the workforce come to work on an average work day. The commuter factor is calculated using average dwell time.</Text>
                  <Text>- <span style={{ fontWeight: 'bold' }}>Location</span> shows in what kind of location the company site is located, including isolated, urban, industrial area and near border.</Text>
                  <Heading level={4} style={{ paddingTop: '10px' }}>Estimated Revenue</Heading>
                  <Text>Estimated Revenue is based on the number of daily commuters, the number of commuters that have a great alternative to the car, average take-rates computed based on real-life reference projects and the average job ticket costs in the tarif zone.</Text>
                  <Heading level={4} style={{ paddingTop: '10px' }}>Modal Split ÖV Trend</Heading>
                  <Text>Shows how the public transport modal split is developing.</Text>
                  <Heading level={4} style={{ paddingTop: '10px' }}>Maps: Company Sites</Heading>
                  <img src={require('../assets/img/companySiteMaps.jpg')} style={{ width: '100%', paddingTop: '5px', paddingBottom: '5px' }}></img>
                  <Text>Displays the company location or the company employee data, depending on the drop down menu:</Text>
                  <Text>- <span style={{ fontWeight: 'bold' }}>Company Site: Location</span> shows the polygon that is used to compute the company site data. This allows users to quickly confirm if the polygon has been correctly chosen, and to flag if there are issues (such as a parking lot that is outside of the analysed polygon)</Text>
                  <Text>- <span style={{ fontWeight: 'bold' }}>Company Site: Employees </span> shows the data points from the Swiss Federal Statistical Office’s Structural business statistics that the cockpit uses to establish the number of employees in the company site. Due to the high spatial resolution of the data, larger companies might comprise more than one data point. The view allows users to confirm if all relevant data points are included in the analysis.</Text>
                  <Heading level={4} style={{ paddingTop: '10px' }}>Modal Split Aggregation</Heading>
                  <img src={require('../assets/img/modalsplitaggregation.jpg')} style={{ width: '100%', paddingTop: '5px', paddingBottom: '5px' }}></img>
                  <Text>The bar chat displays the ÖV, MIV and LV modal split. The black bracket indicates the confidence range. On mouse-over, the modal split percentage and the confidence range will be displayed. The dropdown menu provides different modal split aggregations:</Text>
                  <Text>- <span style={{ fontWeight: 'bold' }}>Aggregation: Dwell Times </span> are computed based on mobile phone mobility data. The blue bars indicate how many people are counted in the company location over 24 hours during an average working day. The red bars indicate dwell time, counting how many people have stayed in the company location for more than 30m in any given hour. Dwell time is used to filter visitors to estimated how many people are actually working at the company. Dwell time is used to estiimate the commuter factor, which indicates how many percent of the employees come to work on an average work day.</Text>
                  <Heading level={4} style={{ paddingTop: '10px' }}>Main Commuter Origins</Heading>
                  <img src={require('../assets/img/MainCommuterOrigins.jpg')} style={{ width: '100%', paddingTop: '5px', paddingBottom: '5px' }}></img>
                  <Text>The table lists where commuters to the company sites come from, displaying the municipality where they start their commute, the number of daily commuter trips from that municipality and for how many percent of the total commuting trips originate from that municipality. The commuter Origin analysis provides a quick indication of which connections matter most if it comes to providing employees a good alternative to car commuting.</Text>
                  <Heading level={4} style={{ paddingTop: '10px' }}>ÖV Quality</Heading>
                  <img src={require('../assets/img/OVQuality.jpg')} style={{ width: '100%', paddingTop: '5px', paddingBottom: '5px' }}></img>
                  <Text>The ÖV Quality Map shows where commuters to the company sites are coming from, on a one square kilometer grid. For each 1km tile, the cockpit calculates the ÖV Quality. Connections with a great ÖV Quality are green, connections with a decent ÖV Quality are yellow, and those with a bad ÖV Quality are red. The cockpit computes likely commutes based on mobile phone data and routing algorithms. The transparency level of the tiles indicates how likely it is that people commute from the tile. A dark red tile, for example indicates that it is highly likley that people commute from that tile, while a light red tile indicates that some people might commute from that tile.</Text>
                  <Text>Below the map, the <span style={{ fontWeight: 'bold' }}>percentage of connections</span> with great, decent and bad ÖV connections is displayed, alongside the ÖV Quality confidence range.</Text>
                  <Heading level={4} style={{ paddingTop: '5px' }}>ÖV Quality Definitions</Heading>
                  <Text>- Green (Great car alternative)</Text>
                  <Text>The connection takes up to 130% (or maximum 10 Minutes) longer than the car travel time, and requires a maximum of one change over.</Text>
                  <Text>- Yellow (Acceptable car alternative)</Text>
                  <Text>The connection takes between 130% and 200% longer than the car travel time, and requires a maximum two change over.</Text>
                  <Text>- Red (Bad car alternative)</Text>
                  <Text>The connection take more than 200% longer than the car travel time.</Text>

                </Drawer.Body>
              </Drawer>
              <Container>

                <Container>
                  {/* Header */}
                  <Header className="page-header">
                    <NavBarComponent />
                  </Header>

                  {/* Content */}
                  <Content style={{
                    backgroundColor: 'white',
                    padding: '20px 30px'
                  }}>
                    <Row align="left">
                      <IconButton icon={<ArrowLeftLineIcon />}
                        //onClick={() => this.handleDocumentation()} 
                        onClick={() => { window.open(URL_FRONT + 'b2b', '_self') }}>
                        Back to Overview
                      </IconButton>
                    </Row>
                    <Row>
                      <Col xs={24} md={16} style={{ paddingBottom: '30px' }}>
                        <HeadingGroup align='left'>
                          <Heading level={1}>Company: {this.state.details?.company_name}</Heading>
                          <Heading level={5}>Site: {this.state.details?.site_name} - {this.state.details?.municipality}, {this.state.details?.plz}, {this.state.details?.kanton}</Heading>
                          <Heading level={6}>Location: {numberWithCommas(this.state.details?.type)}</Heading>
                        </HeadingGroup>
                      </Col>
                      <Col xs={24} md={8} style={{ paddingBottom: '30px' }} align="right">
                        <Row style={{ paddingBottom: '10px' }}>
                          <IconButton icon={<InfoOutlineIcon />}
                            //onClick={() => this.handleDocumentation()} 
                            onClick={() => { window.open('https://42hacks.notion.site/Detailed-View-19763b82658181b29dafcf596a6dc224', "_blank", "noreferrer"); }}>
                            Help
                          </IconButton>
                        </Row>
                        <Row style={{ paddingBottom: '10px' }}>
                          <ExportExcel
                            getExcelData={() => this.getDetailExportData()}
                            fileName={exportFileName}
                            name="Download Analysis Excel"
                            sheetName="company_analysis"
                            onError={() => this.setState({ downloadError: 'The Excel export could not be generated. Check the network requests for the missing year.' })}
                          />
                        </Row>
                        {
                          this.state.downloadError
                            ?
                            <Row style={{ paddingBottom: '10px' }}>
                              <Text style={{ color: '#a94442' }}>{this.state.downloadError}</Text>
                            </Row>
                            :
                            <></>
                        }
                        <Row>

                          <SelectPicker
                            data={this.state.years}
                            label="Year"
                            searchable={false}
                            defaultValue={this.state.current_year}
                            cleanable={false}
                            onChange={this.handleSelectPickerYear.bind(this)}
                            block
                          />
                        </Row>
                      </Col>
                    </Row>

                    <Row gutter={30}>
                      <Col xs={24} md={4} style={{ paddingBottom: '30px' }}>
                        <Panel className="panel-gradient-blue" shaded>

                          <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                            <Heading level={4} style={{ color: "white" }}>{numberWithCommas((this.state.details?.employees).toFixed(0))}</Heading>
                            <Heading level={6} style={{ color: "white" }}>Employees</Heading>
                          </HeadingGroup>
                        </Panel>
                      </Col>

                      <Col xs={24} md={5} style={{ paddingBottom: '30px' }}>
                        <Panel className="panel-gradient-pink" shaded>

                          <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                            <Heading level={4} style={{ color: "white" }}>{numberWithCommas(this.state.details?.total_onsite_trips.toFixed(0))}</Heading>
                            <Heading level={6} style={{ color: "white" }}>Daily Commuters</Heading>
                          </HeadingGroup>
                        </Panel>
                      </Col>

                      <Col xs={24} md={5} style={{ paddingBottom: '30px' }}>
                        <Panel className="panel-gradient-purple" shaded>

                          <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                            <Heading level={4} style={{ color: "white" }}>{numberWithCommas(this.state.details?.total_pkm.toFixed(0))}</Heading>
                            <Heading level={6} style={{ color: "white" }}>PKm per day</Heading>
                          </HeadingGroup>
                        </Panel>
                      </Col>

                      <Col xs={24} md={5} style={{ paddingBottom: '30px' }}>
                        <Panel className="panel-gradient-teal" shaded>

                          <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                            <Heading level={4} style={{ color: "white" }}>{(numberWithCommas(this.state.details?.potential?.new_revenue.toFixed(0)))} CHF</Heading>
                            <Heading level={6} style={{ color: "white" }}>Potential New Revenue</Heading>
                          </HeadingGroup>
                        </Panel>
                      </Col>

                      <Col xs={24} md={5} style={{ paddingBottom: '30px' }}>
                        {this.state.details?.modal_split_trend.trend === 'up' ?
                          <>
                            <Panel className="panel-gradient-green" shaded>

                              <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                                <Heading level={4} style={{ color: "white" }}> <BsArrowUpRightSquareFill style={{ color: 'white' }} /> {numberWithCommas((this.state.details?.modal_split_trend.value).toFixed(2))}%</Heading>
                                <Heading level={6} style={{ color: "white" }}>Modal Split Trend</Heading>
                              </HeadingGroup>

                            </Panel>
                          </>
                          :
                          <>
                            {this.state.details?.modal_split_trend.trend === 'down' ?
                              <>
                                <Panel className="panel-gradient-red" shaded>

                                  <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                                    <Heading level={4} style={{ color: "white" }}> <BsArrowDownRightSquareFill style={{ color: 'white' }} /> {numberWithCommas((this.state.details?.modal_split_trend.value).toFixed(2))}%</Heading>
                                    <Heading level={6} style={{ color: "white" }}>Modal Split Trend</Heading>
                                  </HeadingGroup>

                                </Panel>
                              </>
                              :
                              <>
<Panel className="panel-gradient-orange" shaded>

<HeadingGroup align="left" style={{ marginBottom: 0 }}>
  <Heading level={4} style={{ color: "white" }}> <BsArrowRightSquareFill style={{ color: 'white' }} /> {numberWithCommas((this.state.details?.modal_split_trend.value).toFixed(0))}%</Heading>
  <Heading level={6} style={{ color: "white" }}>Modal Split Trend</Heading>
</HeadingGroup>

</Panel>
                              </>
                            }
                          </>
                        }

                      </Col>
                    </Row>

                    <Row gutter={30} >
                      <Col xs={24} md={24} style={{ paddingBottom: '30px' }}>
                        <Panel shaded>
                          <HeadingGroup align="left" style={{ marginBottom: '0px' }}>
                            <Heading level={3}>ÖV Quality</Heading>
                          </HeadingGroup>
                          <CheckboxGroup name="options" value={this.state.options} inline align="right" onChange={this.handleOVOptions.bind(this)}>
                            <Checkbox defaultChecked value="color">Status Quo</Checkbox>
                            <Checkbox value="color_last_mile">Last Mile</Checkbox>
                            <Checkbox value="color_bike">Bicycle</Checkbox>
                            <Checkbox value="color_working">Working</Checkbox>
                          </CheckboxGroup>
                          <Col xs={24} md={24} style={{ paddingBottom: '10px' }}>
                            <div
                              ref={this.mapContainerOVQuality}
                              style={{
                                width: '100%',
                                height: '500px',
                                backgroundColor: 'lightgrey'
                              }}
                            >
                              {
                                this.state.loading_map
                                  ?
                                  <Loader backdrop content="loading..." vertical />
                                  :
                                  <></>
                              }
                            </div>
                            <Text muted as="i" >Data computed based on Swisscom and 42hacks' GNN Algorithm</Text>

                          </Col>
                          {/* <Col xs={24} md={8} style={{ paddingBottom: '10px' }}>
                              <canvas ref={this.chartRefDonut} />
                              </Col> */}

                          <Row gutter={30} style={{ paddingTop: '30px' }}>
                            <Col xs={24} md={24}>
                              <Row>
                                <Col xs={24} md={8}>
                                  <Panel className="green-ov-quality" shaded>
                                    <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                                      <Heading level={3} style={{ color: "#0A5E05" }}>{(this.state.donutpieinfo.green_percentage * 100).toFixed(0)}%</Heading>
                                      <Heading level={5} style={{ color: "#0A5E05" }}>Great ÖV Quality</Heading>
                                      {/* <Heading level={6} style={{ color: "#0A5E05" }}>Std. Deviation: ±{(((this.state.details?.green_ov_quality.MIV.std + this.state.details?.green_ov_quality.LV.std + this.state.details?.green_ov_quality.OV.std) / this.state.details?.total_trips) * 100).toFixed(1)}%</Heading> */}
                                    </HeadingGroup>
                                  </Panel>
                                </Col>

                                <Col xs={24} md={8}>
                                  <Panel className="yellow-ov-quality" shaded>
                                    <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                                      <Heading level={3} style={{ color: "#5E3605" }}>{(this.state.donutpieinfo.yellow_percentage * 100).toFixed(0)}%</Heading>
                                      <Heading level={5} style={{ color: "#5E3605" }}>Decent ÖV Quality</Heading>
                                      {/* <Heading level={6} style={{ color: "#5E3605" }}>Std. Deviation: ±{(((this.state.details?.yellow_ov_quality.MIV.std + this.state.details?.yellow_ov_quality.LV.std + this.state.details?.yellow_ov_quality.OV.std) / this.state.details?.total_trips) * 100).toFixed(1)}%</Heading> */}
                                    </HeadingGroup>
                                  </Panel>
                                </Col>

                                <Col xs={24} md={8}>
                                  <Panel className="red-ov-quality" shaded>
                                    <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                                      <Heading level={3} style={{ color: "#5f0505" }}>{(this.state.donutpieinfo.red_percentage * 100).toFixed(0)}%</Heading>
                                      <Heading level={5} style={{ color: "#5f0505" }}>Bad ÖV Quality</Heading>
                                      {/* <Heading level={6} style={{ color: "#5f0505" }}>Std. Deviation: ±{(((this.state.details?.red_ov_quality.MIV.std + this.state.details?.red_ov_quality.LV.std + this.state.details?.red_ov_quality.OV.std) / this.state.details?.total_trips) * 100).toFixed(1)}%</Heading> */}
                                    </HeadingGroup>
                                  </Panel>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </Panel>
                      </Col>
                    </Row>

                    <Row gutter={30}>
                      <Col xs={24} md={12} style={{ paddingBottom: '30px' }}>
                        <Panel shaded style={{ height: '100%' }}>

                          <SelectPicker size="lg" block onChange={this.handleSelectPicker.bind(this)} defaultValue={this.state.current_aggregation} cleanable={false} searchable={false} data={this.state.aggregation_types} label="Aggregation" style={{ paddingBottom: '10px' }} />

                          <canvas ref={this.chartRef} />
                        </Panel>

                      </Col>
                      <Col xs={24} md={12} style={{ paddingBottom: '30px' }}>
                        <Panel shaded tyle={{ height: '100%' }}>
                          <HeadingGroup align="left" style={{ marginBottom: 0 }}>
                            <Heading level={3}>Main Commuter Origins to {this.state.details?.company_name}</Heading>
                          </HeadingGroup>

                          <Table height={400}
                            data={this.state.top_ods}>
                            <Column flexGrow={0.5} align="left" fixed fullText>
                              <HeaderCell style={{ background: '#F6F6F6', fontSize: '14px', fontWeight: 600, color: '#000000' }}>Nr.</HeaderCell>
                              <Cell>
                                {(rowData) => {
                                  return <div>{rowData.position}</div>;
                                }}
                              </Cell>
                            </Column>

                            <Column flexGrow={2} align="left" fixed fullText>
                              <HeaderCell style={{ background: '#F6F6F6', fontSize: '14px', fontWeight: 600, color: '#000000' }}>Municipality (PLZ)</HeaderCell>
                              <Cell>
                                {(rowData) => {
                                  return <div>{rowData._id} ({rowData.PLZ})</div>;
                                }}
                              </Cell>
                            </Column>

                            <Column flexGrow={1} align="left" fullText>
                              <HeaderCell style={{ background: '#F6F6F6', fontSize: '14px', fontWeight: 600, color: '#000000' }}># of Commuters</HeaderCell>
                              <Cell>
                                {(rowData) => {
                                  return <div>{numberWithCommas(rowData.people.toFixed(0))}</div>;
                                }}
                              </Cell>
                            </Column>

                            <Column flexGrow={2} align="left" fullText>
                              <HeaderCell style={{ background: '#F6F6F6', fontSize: '14px', fontWeight: 600, color: '#000000' }}>Percentage of Total Trips (%)</HeaderCell>
                              <Cell>
                                {(rowData) => {
                                  return <div>{(rowData.pct_trips * 100).toFixed(0)}%</div>;
                                }}
                              </Cell>
                            </Column>
                          </Table>


                        </Panel>
                      </Col>
                    </Row>

                    <Row gutter={30}>
                      <Col xs={24} md={24} style={{ paddingBottom: '30px' }}>
                        <Panel shaded>
                          <SelectPicker size="lg" block defaultValue={this.state.current_maps} onChange={this.handleSelectMap.bind(this)} cleanable={false} searchable={false} data={this.state.map_types} label="Company Site" style={{ paddingBottom: '10px' }} />
                          <div
                            ref={this.mapContainer}
                            style={{
                              width: '100%',
                              height: '350px',
                              backgroundColor: 'lightgrey'
                            }}
                          />
                        </Panel>
                      </Col>
                    </Row>

                  </Content>
                </Container>
              </Container>
            </>

        }

      </>
    )
  }
}

export default (props) => (
  // eslint-disable-next-line react/jsx-pascal-case
  <BusinessCockpitDetails
    {...props}
    params={useParams()}
  />
); 
